import { Component } from "../lib/component.ts";

interface Font {
  name: string;
  method?: string;
}

interface PickerTuning {
  scale?: number;
  offsetX?: number;
  offsetY?: number;
}

// Per-font picker sample tuning. The "W" sample is rendered at 34px; scale
// nudges the rendered size and offsetX/offsetY (em units) compensate for
// off-center glyphs (e.g. script faces with a leading swash, or display
// faces whose visual center sits high). Adjust here, not in configs.
const PICKER_TUNING: Record<string, PickerTuning> = {
  "Sackers Gothic": {
    scale: 0.925,
  },
  "Pearl Mémoire": {
    scale: 0.88,
    offsetX: -0.1,
  },
  "Pearl Deboss": {
    scale: 0.88,
    offsetX: -0.1,
  },
  "Muse Script": {
    scale: 1.1,
    offsetY: 0.075,
  },
};

// Customer-facing labels for the font picker, cart line items, and checkout. The
// canonical `name` keeps identifying the @font-face family and is what's
// stored in `_personalization` restore data; this map only controls what the
// shopper sees. Fonts without an entry fall back to their canonical name.
const FONT_DISPLAY_NAMES: Record<string, string> = {
  "Pearl Mémoire": "Pearl",
  "Pearl Deboss": "Pearl",
  "Muse Script": "Muse",
  "Sackers Gothic": "Archive",
};

let uid = 0;

class FontPicker extends Component {
  #name = `font-picker-${++uid}`;

  #fonts: Font[] = [];

  #methodLabels: Record<string, string> = {};

  #value = "";

  get value(): string {
    const checked = this.querySelector("input:checked");
    if (checked instanceof HTMLInputElement) return checked.value;
    return this.#value;
  }

  set value(val: string) {
    this.#value = val;
    const radio = this.querySelector(`input[value="${CSS.escape(val)}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
    this.#updateSelection();
  }

  getDisplayName(value: string): string {
    return FONT_DISPLAY_NAMES[value] ?? value;
  }

  getCartLabel(value: string): string {
    const display = this.getDisplayName(value);
    const method = this.#fonts.find((f) => f.name === value)?.method ?? "";
    const methodLabel = this.#methodLabels[method] ?? method;
    return methodLabel ? `${display} (${methodLabel})` : display;
  }

  connectedCallback(): void {
    super.connectedCallback();
    const raw = this.dataset.methodLabels;
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          this.#methodLabels = Object.fromEntries(
            Object.entries(parsed).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string",
            ),
          );
        }
      } catch {
        // Ignore malformed maps; method slugs fall back to themselves.
      }
    }
    this.addEventListener("change", this.#handleChange, { signal: this.signal });
  }

  setFonts(fonts: Font[], defaultFont: string): void {
    this.#fonts = fonts;
    const legend = this.getAttribute("legend") ?? "Font options";

    const fieldset = document.createElement("fieldset");
    fieldset.className = "font-picker";

    const legendEl = document.createElement("legend");
    legendEl.className = "font-picker__legend h4";
    legendEl.textContent = legend;
    fieldset.appendChild(legendEl);

    const grid = document.createElement("div");
    grid.className = "font-picker__grid";
    grid.style.setProperty("--font-picker-columns", String(fonts.length));

    fonts.forEach((f, i) => {
      const col = String(i + 1);
      const optionId = `${this.#name}-opt-${i}`;
      const nameId = `${this.#name}-name-${i}`;
      const displayName = this.getDisplayName(f.name);
      const methodSlug = f.method ?? "";
      const methodLabel = this.#methodLabels[methodSlug] ?? methodSlug;

      const option = document.createElement("label");
      option.className = "font-picker__option";
      option.dataset.font = f.name;
      option.style.gridColumn = col;
      option.style.gridRow = "1";

      const input = document.createElement("input");
      input.type = "radio";
      input.id = optionId;
      input.name = this.#name;
      input.value = f.name;
      input.className = "visually-hidden";
      input.setAttribute("aria-labelledby", nameId);
      option.appendChild(input);

      const sample = document.createElement("span");
      sample.className = "font-picker__sample";
      sample.setAttribute("aria-hidden", "true");
      sample.textContent = "W";
      sample.style.fontFamily = f.name;
      const tuning = PICKER_TUNING[f.name];
      if (tuning?.scale) sample.style.setProperty("--font-picker-scale", String(tuning.scale));
      if (tuning?.offsetX)
        sample.style.setProperty("--font-picker-offset-x", `${tuning.offsetX}em`);
      if (tuning?.offsetY)
        sample.style.setProperty("--font-picker-offset-y", `${tuning.offsetY}em`);
      option.appendChild(sample);

      grid.appendChild(option);

      const details = document.createElement("label");
      details.className = "font-picker__details";
      details.htmlFor = optionId;
      details.dataset.font = f.name;
      details.style.gridColumn = col;
      details.style.gridRow = "2";

      const nameSpan = document.createElement("span");
      nameSpan.className = "font-picker__name";
      nameSpan.id = nameId;
      nameSpan.textContent = displayName;
      details.appendChild(nameSpan);

      const methodSpan = document.createElement("span");
      methodSpan.className = "font-picker__method";
      methodSpan.textContent = methodLabel;
      details.appendChild(methodSpan);

      grid.appendChild(details);
    });

    // Trailing spacer keeps the underline running past the last column.
    const spacer = document.createElement("div");
    spacer.className = "font-picker__option font-picker__option--spacer";
    spacer.setAttribute("aria-hidden", "true");
    spacer.style.gridColumn = String(fonts.length + 1);
    spacer.style.gridRow = "1";
    grid.appendChild(spacer);

    fieldset.appendChild(grid);

    this.replaceChildren(fieldset);
    this.value = defaultFont;
  }

  #handleChange = (): void => {
    const checked = this.querySelector("input:checked");
    if (checked instanceof HTMLInputElement) {
      this.#value = checked.value;
      this.#updateSelection();
    }
  };

  #updateSelection(): void {
    const elements = this.querySelectorAll<HTMLElement>("[data-font]");
    for (const el of elements) {
      if (el.dataset.font === this.#value) {
        el.dataset.selected = "true";
      } else {
        delete el.dataset.selected;
      }
    }
  }
}

if (!customElements.get("font-picker")) {
  customElements.define("font-picker", FontPicker);
}
